import { useEffect, useRef, useState } from 'react';
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, TableBody, TableRow, Typography } from '@mui/material';

import { Timestamp } from '@bufbuild/protobuf';
import {
  ActionsPopup,
  ContentDetailCard,
  HwProceedButton,
  IStyles,
  IconWrapper,
  ImageWrapper,
  InPageHeader,
  InfoBar,
  InstructionsPopup,
  PrimaryButton,
  SecondaryButton,
  deserify,
  getLocalStorage,
  getMediaBasePath,
  getResourceCategory,
  getTeacherSubjectEnum,
  pxToRem,
  pxTovW,
  resourceTypeName,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { ResourceInfo } from '@protos/content_management/content.common.apis_pb';
import {
  CreationStatusEnum,
  ResourceCategoryEnum,
  Resource_ResourceEnum,
} from '@protos/content_management/content.db_pb';
import {
  SessionModeEnum,
  SessionStatusEnum,
} from '@protos/learning_management/lms.db_pb';
import {
  LessonContent,
  LessonInfo,
  TeacherLessonInfo,
} from '@protos/learning_management/lms.lesson.common.apis_pb';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import {
  ADD_RESOURCES,
  REPLACE_RESOURCE,
  TEACHING_FLOW,
} from '../../../routeHandling/RoutesNomenclature';
import { findClassSubjectInfoFromClassSection } from '../../../utils/functions';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import {
  setLessonContent,
  setSelectedLessonInfo,
} from '../reducer/teach.slice';
import { aiLessonUpdateEvent } from '../teach.events';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { interactionEvent } from '../../Auth/auth.events';

const styles: IStyles = {
  root: {
    backgroundColor: 'neutral.paleGrey',
    // height: '100vh',
    marginBottom: pxTovW(10),
    padding: { xs: pxToRem(20), md: `${pxTovW(20)}`, lg: 0 },
    boxSizing: 'border-box',
  },
  headingImage: {
    width: { xs: pxToRem(60), md: pxTovW(105) },
    height: { xs: pxToRem(60), md: pxTovW(105) },
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    objectFit: 'cover',
  },
  mainContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: { md: 'space-between', lg: 'unset' },
    gap: { xs: pxToRem(40), md: pxToRem(20), lg: pxTovW(270) },
    padding: { lg: `${pxTovW(15)} ${pxTovW(240)}` },
  },
  leftPanel: {
    width: { md: '60%', lg: pxTovW(444) },
    display: 'flex',
    flexDirection: 'column',
    gap: { md: pxTovW(30) },
  },
  rightPanel: {
    // border:"1px solid red",
    height: { xs: '75vh', md: '100vh' },
    overflow: 'auto',
    paddingX: pxTovW(20),
    flexBasis: { md: '100%', lg: pxTovW(747) },
  },
  infoBox: {},
  editButton: {
    cursor: 'pointer',
    padding: 0,
    minWidth: 0,
    width: { xs: pxToRem(34), md: pxTovW(50) },
    height: { xs: pxToRem(34), md: pxTovW(50) },
    borderRadius: pxTovW(8),
    bgcolor: 'common.white',
    border: '1px solid #EEEEEE',
  },
  cardsContainer: { display: 'block' },
  addButton: {
    borderRadius: { xs: pxToRem(8), md: pxTovW(10) },
    width: { xs: pxToRem(80), md: pxTovW(120) },
    height: { xs: pxToRem(38), md: pxTovW(58) },
    background: ' #FFFFFF 0% 0% no-repeat padding-box',
    boxShadow: '0px 3px 6px #0000000A',
    border: '1px solid #EEEEEE',
    color: 'secondary.main',
    fontSize: { xs: pxToRem(14), md: pxTovW(21) },
    position: 'absolute',
    transform: 'translate(-50%,-50%)',
    '&:hover': {
      background: '#FFFFFF 0% 0% no-repeat padding-box',
    },
  },
  lineAndButton: {
    margin: { xs: `${pxToRem(25)} 0`, md: `${pxTovW(40)} 0` },
    height: { xs: '.5vw', md: '0.146vw' }, //2px
    background: '#EEEEEE',
    textAlign: 'center',
    position: 'relative',
  },
  button: {},
};

export default function EditLessonPlan() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isOffline } = useDownloadContext();
  const { setSelectedFunction } = useGlobalContext();
  const [openIntructionsPopup, setOpenIntructionsPopup] = useState(false);
  const teacher_id = getLocalStorage('userId');
  const { subject_id, topic_id, lesson_id, chapter_id } = useParams();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const class_id =
    new URLSearchParams(location.search).get('classId') || undefined;
  const section_id = new URLSearchParams(location.search).get('sectionId');
  const section = new URLSearchParams(location.search).get('section');
  const lesson_session_id =
    new URLSearchParams(location.search).get('lessonSessionId') || undefined;
  const class_subject_info =
    deserify(
      useAppSelector((state) => state.homeDashboard.class_subject_info)
    ) ||
    findClassSubjectInfoFromClassSection({
      userInfo: user_info,
      classId: Number(class_id),
      sectionId: (section_id ? BigInt(section_id) : undefined),
      subjectId: Number(subject_id),
    });
  const { LessonTeachAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();

  // Copy of the selected Lesson Plan
  const { selected_lessons_info, session_mode_teaching_flow } = deserify(
    useAppSelector((state) => state.teach)
  );
  // console.log('selected_lessons_info: ', selected_lessons_info);

  // useEffect(() => {
  //   aiLessonOpenEvent(lesson_id);
  // }, []);
  const [goingBackPopup, setGoingBackPopup] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const popupClose = () => {
    setRemoveIndex(-1);
    setOpenDialog(false);
  };

  // --------------------------------------------------------------------------

  const addClickHandler = (resourceIndex: number) => {
    const params: Record<string, string> = {};
    if (class_id) params['classId'] = class_id;
    if (section_id) params['sectionId'] = section_id;
    if (section) params['section'] = section;
    navigate({
      pathname: `${ADD_RESOURCES}/${subject_id}/${chapter_id}/${topic_id}/${lesson_id}/${resourceIndex}`,
      search: Object.keys(params).length
        ? `?${createSearchParams(params)}`
        : undefined,
    });
  };

  // --------------------------------------------------------------------------

  const removeClickHandler = (resourceIndex: number) => {
    // it will be deleted in deleteCLickHandler after popup opens
    setRemoveIndex(resourceIndex);
    setOpenDialog(true);
  };

  // --------------------------------------------------------------------------

  const replaceClickHandler = (resourceIndex: number) => {
    const params: Record<string, string> = {};
    if (class_id) params['classId'] = class_id;
    if (section_id) params['sectionId'] = section_id;
    if (section) params['section'] = section;
    navigate({
      pathname: `${REPLACE_RESOURCE}/${subject_id}/${chapter_id}/${topic_id}/${lesson_id}/${resourceIndex}`,
      search: Object.keys(params).length
        ? `?${createSearchParams(params)}`
        : undefined,
    });
  };

  // --------------------------------------------------------------------------

  const saveClickHandler = async () => {
    // console.log('saveClickHandler: ', selected_lessons_info);
    if (selected_lessons_info) updateLesson(selected_lessons_info);
    await interactionEvent({
      context: 'Teaching_flow',
      name: 'SAVE',
      pathSegments: pathSegments,
      isOffline: isOffline,
    });
  };

  // --------------------------------------------------------------------------

  async function updateLesson(selectedLesson: LessonInfo | TeacherLessonInfo) {
    try {
      // setLoading(true);
      const resIds = lessons_content?.resources
        .sort((a, b) => a.rank - b.rank)
        .map((val) => val.resourceId);

      if (resIds?.length === 0) {
        dispatch(
          setToastInfo({
            variant: 'error',
            label: 'Please add at least one resource.',
            open: true,
          })
        );
        return;
      }
      //calculate estimated time

      const response =
        await LessonTeachAPIServiceV1ClientWithStatusCodeHandler.upsertLesson({
          lessonId: lesson_id,
          teacherId: BigInt(teacher_id),
          title: selectedLesson.title,
          subjectId: Number(subject_id),
          moduleId: selectedLesson.moduleId,
          moduleCategory: selectedLesson.moduleCategory,
          posterImageUrl: selectedLesson.posterImageUrl,
          resourceIds: resIds,
          // sourceLessonId: Number(selectedLesson.lessonId),
          // sourceLessonId: selectedLesson.lessonId,
          creationStatus: CreationStatusEnum.CREATION_STATUS_APPROVED,
        });

      if (response?.data) {
        const data = response.data;
        // console.log('updateLesson', data);
        // console.log(typeof data, data);
        dispatch(setSelectedLessonInfo(data));

        await aiLessonUpdateEvent({
          lessonId: lesson_id,
          resourceIds: resIds,
          isOffline: isOffline,
        });
        const prevSessionInfo =
          await LessonTeachAPIServiceV1ClientWithStatusCodeHandler.getPreviousLessonSessionInfo(
            {
              teacherId: BigInt(teacher_id),
              lessonId: data.lessonId,
            }
          );

        const newSessionInfo =
          await LessonTeachAPIServiceV1ClientWithStatusCodeHandler.createTeacherLessonSession(
            {
              teacherId: BigInt(teacher_id),
              lessonId: data.lessonId,
              schoolId: user_info?.schoolDetails[0]?.schoolId,
              academicYear: 0,
              classId: class_subject_info?.classId,
              section: class_subject_info?.section,
              subject:
                subject_id && !isNaN(Number(subject_id))
                  ? getTeacherSubjectEnum(
                      Number(subject_id),
                      user_info?.teachClassSubjects
                    )
                  : undefined,
              // teacherLessonSessionId: ,
              sessionResourceIds:
                prevSessionInfo.data?.completedResourceIds || [],
              startTime: Timestamp.fromDate(new Date()),
              sessionStatus: SessionStatusEnum.SESSION_STATUS_STARTED,
              sessionMode:
                session_mode_teaching_flow ===
                SessionModeEnum.SESSION_MODE_TEACH
                  ? SessionModeEnum.SESSION_MODE_LESSON_RESOURCE
                  : SessionModeEnum.SESSION_MODE_PREPARE_RESOURCE,
            }
          );
        const params: Record<string, string> = {
          lessonSessionId:
            newSessionInfo.teacherLessonSessionId.toString() || '',
        };
        if (class_id) params['classId'] = class_id;
        if (section_id) params['sectionId'] = section_id;
        if (section) params['section'] = section;
        navigate({
          pathname: `${TEACHING_FLOW}/${subject_id}/${chapter_id}/${topic_id}/${data.lessonId}`,
          search: `?${createSearchParams(params)}`,
        });
      } else {
        // setError(new Error('Login failed'));
      }

      // setLoading(false);
    } catch (error: any) {
      // setLoading(false);
      // setError(err);
      dispatch(
        setToastInfo({
          label: error.message
            ? `${error.message.replace(/\[.*?\]/g, '')}`
            : 'Something went wrong',
          variant: 'error',
          open: true,
        })
      );
    }
  }

  // --------------------------------------------------------------------------

  // Getting all the Lesson content
  const { lessons_content } = deserify(useAppSelector((state) => state.teach));
  // console.log('lessons_content: ', lessons_content);

  //^ delete the item required
  const [removeIndex, setRemoveIndex] = useState(-1);
  const deleteCLickHandler = () => {
    // creating a copy and removing required resource
    const temp_lessons_content: LessonContent = JSON.parse(
      JSON.stringify(lessons_content)
    );
    const deletedTitle = temp_lessons_content.resources[removeIndex].title;
    temp_lessons_content.resources.splice(removeIndex, 1);

    // fixing rank
    temp_lessons_content.resources.forEach((content, index) => {
      temp_lessons_content.resources[index].rank = index + 1;
    });

    dispatch(
      setLessonContent({
        ...lessons_content,
        resources: temp_lessons_content.resources,
      })
    );

    dispatch(
      setToastInfo({
        variant: 'info',
        label: `Deleted resource - ${deletedTitle}`,
        open: true,
      })
    );

    setRemoveIndex(-1);
    popupClose();
  };

  // --------------------------------------------------------------------------

  //^ Drag and drop
  const dragItem = useRef(0);
  const dragOverItem = useRef(0);

  // will be used to adjust styling of the item over which it is hovering
  const [dragOverItemIndex, setdragOverItemIndex] = useState(-1);

  const dragStart = (position: number) => {
    // record the position from where the item is picked
    dragItem.current = position;
  };

  const dragEnter = (position: number) => {
    // record the position above which the item is present
    dragOverItem.current = position;
    setdragOverItemIndex(position);
  };

  const drop = () => {
    const temp_lessons_content: LessonContent = JSON.parse(
      JSON.stringify(lessons_content)
    );
    // create a copy of the item being dragged
    const dragItemContent = temp_lessons_content.resources[dragItem.current];
    // remove the item being dragged from the list
    temp_lessons_content.resources.splice(dragItem.current, 1);
    // add the item to its new position
    temp_lessons_content.resources.splice(
      dragOverItem.current,
      0,
      dragItemContent
    );
    // reset the values
    dragItem.current = -1;
    dragOverItem.current = -1;
    setdragOverItemIndex(-1);
    // fixing rank
    temp_lessons_content.resources.forEach((content, index) => {
      temp_lessons_content.resources[index].rank = index + 1;
    });

    // set the new array
    dispatch(
      setLessonContent({
        ...lessons_content,
        resources: temp_lessons_content.resources,
      })
    );
  };

  // --------------------------------------------------------------------------

  const totalTimeOfResources = lessons_content?.resources.reduce(
    (acc, resource) => acc + resource.estimatedTimeInMin,
    0
  );

  // --------------------------------------------------------------------------

  const backConfirmationHandler = async () => {
    navigate({
      pathname: `${TEACHING_FLOW}/${subject_id}/${chapter_id}/${topic_id}/${selected_lessons_info?.lessonId}`,
      search: `?${createSearchParams({
        ...(class_id && { classId: class_id }),
        ...(section_id && { sectionId: section_id }),
        ...(section && { section: section }),
        lessonSessionId: (lesson_session_id ?? '').toString(),
      })}`,
    });
  };

  // --------------------------------------------------------------------------

  const backButtonClick = async () => {
    setGoingBackPopup(true);
  };

  useEffect(() => {
    setSelectedFunction(() => backButtonClick);
    return () => {
      setSelectedFunction(null);
    };
  }, [goingBackPopup]);

  // --------------------------------------------------------------------------

  return (
    <Box sx={styles.root}>
      <InPageHeader
        title={<Heading selectedLessonInfo={lessons_content} />}
        buttonText="SAVE"
        // buttonClickHandler={() => navigate(HOME)}
        buttonClickHandler={saveClickHandler}
      />
      <Box sx={styles.mainContainer}>
        <Box sx={styles.leftPanel}>
          {lessons_content?.learningOutcomes &&
            lessons_content.learningOutcomes.length !== 0 && (
              <Box
                sx={{
                  margin: {
                    xs: `${pxToRem(26)} ${pxToRem(4)} ${pxToRem(0)} ${pxToRem(
                      4
                    )}`,
                    md: 0,
                  },
                }}
              >
                <SecondaryButton
                  variant="outlined"
                  witharrow
                  fullWidth
                  onClick={() => setOpenIntructionsPopup(true)}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Learning Outcome
                  </Typography>
                </SecondaryButton>
              </Box>
            )}

          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <InfoBar
              contentList={[
                {
                  iconName: 'questions',
                  heading: lessons_content?.resources.length || 0,
                  subHeading: 'Resources',
                },
                {
                  iconName: 'clock',
                  heading: totalTimeOfResources || 0,
                  subHeading: 'Minutes',
                },
              ]}
            />
          </Box>

          {/* <Box
            onClick={saveClickHandler}
            sx={{ display: { xs: 'none', md: 'block' } }}
          >
            <PrimaryButton fullWidth>SAVE</PrimaryButton>
          </Box> */}
        </Box>

        <Box sx={styles.rightPanel}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h2">Teaching Flow</Typography>
            <Button
              sx={styles.editButton}
              onClick={() => navigate(`${TEACHING_FLOW}/${lesson_id}`)}
              disabled
            >
              <EditOutlinedIcon color="disabled" fontSize="large" />
            </Button>
          </Box>

          <TableBody sx={styles.cardsContainer}>
            {lessons_content?.resources.map(
              (resource: ResourceInfo, resourceIndex: number) => (
                <TableRow
                  key={resourceIndex}
                  draggable={true}
                  onDragStart={() => dragStart(resourceIndex)}
                  onDragEnter={() => dragEnter(resourceIndex)}
                  onDragEnd={drop}
                  sx={{
                    opacity: dragOverItemIndex === resourceIndex ? 0.2 : 1,
                    display: 'block',
                  }}
                >
                  <Box sx={styles.lineAndButton}>
                    <Button
                      sx={styles.addButton}
                      onClick={async () => {
                        addClickHandler(resourceIndex);
                        await interactionEvent({
                          context: 'Teaching_flow',
                          name: 'ADD',
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        });
                      }}
                    >
                      Add
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      height: { xs: pxToRem(127), md: pxTovW(177) },
                      width: '100%',
                      marginTop: { xs: pxToRem(20), md: pxTovW(20) },
                    }}
                  >
                    <Box sx={{ flexBasis: { xs: '10%' } }}>
                      <InterActiveButtonGroup
                        removeClickHandler={async () => {
                          removeClickHandler(resourceIndex);
                          await interactionEvent({
                            context: 'Teaching_flow',
                            name: 'DELETE',
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          });
                        }}
                        replaceClickHandler={async () => {
                          replaceClickHandler(resourceIndex);
                          await interactionEvent({
                            context: 'Teaching_flow',
                            name: 'REPLACE',
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          });
                        }}
                        dragStart={dragStart}
                        dragEnter={dragEnter}
                        drop={drop}
                        resourceIndex={resourceIndex}
                      />
                    </Box>

                    <Box sx={{ flexBasis: { xs: '90%', md: '100%' } }}>
                      <ContentDetailCard
                        variant="large"
                        tagName={getResourceCategory(
                          resource.resourceCategoryType
                        )}
                        image={getMediaBasePath(
                          resource.posterImageUrl,
                          resource.resourceCategoryType ===
                            ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                            ? 'userDataBucket'
                            : 'processedMediaBucket'
                        )}
                        heading={resource.title}
                        showVideoIcon={
                          resource.resourceType ===
                          Resource_ResourceEnum.RESOURCE_TYPE_VIDEO
                        }
                        iconDetails={[
                          {
                            iconName: 'clock',
                            text: `${resource.estimatedTimeInMin} Min`,
                          },
                          {
                            iconName: resourceTypeName(resource.resourceType)
                              .icon,

                            text: resourceTypeName(resource.resourceType).name,
                          },
                        ]}
                        rootStyle={{
                          width: '100%',
                          backgroundColor: 'common.white',
                          // p: { xs: pxToRem(13), md: pxTovW(20) },
                        }}
                      />
                    </Box>
                  </Box>
                </TableRow>
              )
            )}

            <Box sx={styles.lineAndButton}>
              <Button
                sx={styles.addButton}
                onClick={async () => {
                  addClickHandler(lessons_content?.resources?.length || 0);
                  await interactionEvent({
                    context: 'Teaching_flow',
                    name: 'ADD',
                    pathSegments: pathSegments,
                    isOffline: isOffline,
                  });
                }}
              >
                Add
              </Button>
            </Box>
          </TableBody>
        </Box>

        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'fixed',
            bottom: 10,
          }}
          onClick={saveClickHandler}
        >
          <HwProceedButton
            buttonTitle="SAVE"
            clickHandler={saveClickHandler}
            tabs={[
              {
                quantity: lessons_content?.resources.length.toString() || '0',
                title: 'Resources',
              },

              {
                quantity: totalTimeOfResources?.toString() || '',
                title: 'Mins',
              },
            ]}
          />
        </Box>
      </Box>
      <InstructionsPopup
        popupText={lessons_content?.learningOutcomes}
        open={openIntructionsPopup}
        handleClose={() => setOpenIntructionsPopup(false)}
      />

      <ActionsPopup
        open={openDialog}
        handleClose={popupClose}
        iconName="delete"
        fontSmall
        popupText={`Are you sure want to delete`}
        splitText={`${lessons_content?.resources[removeIndex]?.title}?`}
        yesClickHandler={deleteCLickHandler}
        noClickHandler={popupClose}
      />

      <ActionsPopup
        open={goingBackPopup}
        handleClose={() => {
          setGoingBackPopup(false);
        }}
        fontSmall
        iconName="info_red"
        popupText="If you have made any changes, please ensure they are saved before continuing."
        yesText="Return"
        noText="Stay"
        yesClickHandler={async () => {
          backConfirmationHandler();
        }}
        noClickHandler={async () => {
          setGoingBackPopup(false);
        }}
      />
    </Box>
  );
}

interface HProps {
  selectedLessonInfo?: LessonContent;
}
const Heading = ({ selectedLessonInfo }: HProps) => {
  // console.log('selectedLessonInfo:', selectedLessonInfo);

  const dispatch = useAppDispatch();
  const { selected_lessons_info } = deserify(
    useAppSelector((state) => state.teach)
  );

  return (
    <Box sx={{ display: 'flex', gap: { xs: pxToRem(10), md: pxTovW(20) } }}>
      <ImageWrapper
        name="chapterImage"
        type="png"
        styles={styles.headingImage}
        parentFolder="tempAssets"
        path={getMediaBasePath(
          selectedLessonInfo?.lessonPosterImageUrl,
          'processedMediaBucket'
        )}
      />
      {/* <Typography variant="h1">{selectedLessonInfo?.lessonTitle}</Typography> */}

      <Typography variant="h1">
        <Box
          component="input"
          type="text"
          value={selected_lessons_info?.title}
          onChange={(event) => {
            dispatch(
              setSelectedLessonInfo({
                ...selectedLessonInfo,
                title: event.target.value,
              })
            );
          }}
          sx={{
            fontFamily: 'Poppins',
            color: '#1D1D1D',
            fontWeight: '800',
            fontSize: { xs: pxToRem(21), md: pxTovW(42) },

            border: 'none',
            borderBottom: `${pxTovW(2)} solid red`,
            borderColor: 'secondary.main',
            p: { xs: pxToRem(10), md: pxTovW(10) },

            width: { xs: '90%', md: 'unset' },

            '&:focus': {
              borderBottom: `${pxTovW(5)} solid red`,
              outline: 'none',
              borderColor: 'primary.main',
            },
          }}
        />
      </Typography>
    </Box>
  );
};

interface IBCProps {
  removeClickHandler?: () => void;
  replaceClickHandler?: () => void;
  moveClickHandler?: () => void;
  dragStart: (arg: number) => void;
  dragEnter: (arg: number) => void;
  drop: () => void;
  resourceIndex: number;
}
const InterActiveButtonGroup = (props: IBCProps) => {
  const {
    removeClickHandler,
    moveClickHandler,
    replaceClickHandler,
    dragStart,
    dragEnter,
    drop,
    resourceIndex,
  } = props;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-around',
        alignItems: 'center',
        '&>div': { cursor: 'pointer' },
      }}
    >
      <Button onClick={removeClickHandler}>
        <IconWrapper name="remove" size="md" type="png" parentFolder="icons" />
      </Button>

      <Button onClick={replaceClickHandler}>
        <IconWrapper name="refresh" size="md" type="png" parentFolder="icons" />
      </Button>

      <Button
        onClick={moveClickHandler}
        // draggable={true}
        // onDragStart={() => dragStart(resourceIndex)}
        // onDragEnter={() => dragEnter(resourceIndex)}
        // onDragEnd={drop}
        sx={{ cursor: 'grab' }}
      >
        <IconWrapper
          name="reorder"
          size="small"
          type="png"
          parentFolder="icons"
        />
      </Button>
    </Box>
  );
};
